import { styled } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { useEventHandler } from "@kaltura/mediaspace-shared-hooks";
import { KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { KmeCustomItem, KmeCustomComponentInjectedProps } from "./kme-custom-item/KmeCustomItem";
import { CustomComponent } from "@kaltura/mediaspace-shared-ui";

const FullSizeIframe = styled("iframe")({
    width: "100%",
    height: "100%",
    border: 0,
});

export interface MeetingRoomProps {
    // The URL of the iframe that loads the room contents
    roomUrl: string;
    onSessionEnd?: (endedForAll: boolean) => void;
    // optional custom fullScreen toggle
    onToggleFullScreen?: (fullScreen: boolean) => void;
    // optional title for the iframe, used for accessibility
    title?: string;
    // kms customization items
    customKmeItems?: KmeCustomItem[];
}

/**
 * Generic component for rendering a meeting room and handling its post messages.
 */
export const MeetingRoom = ({
    roomUrl,
    onSessionEnd,
    onToggleFullScreen,
    title,
    customKmeItems = [],
}: MeetingRoomProps) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);

    const [customComponent, setCustomComponent] = useState<
        KmsTypeComponent<any, KmeCustomComponentInjectedProps> | undefined
    >();

    // Notify the KME iframe when the main window gains or loses the focus
    useEffect(() => {
        const onFocusChange = (focused: boolean) => {
            iframeRef.current?.contentWindow?.postMessage(
                {
                    eventType: "tab-focus-changed",
                    inFocus: focused,
                },
                "*"
            );
        };

        const onFocus = () => onFocusChange(true);
        const onBlur = () => onFocusChange(false);

        window.addEventListener("focus", onFocus);
        window.addEventListener("blur", onBlur);
        return () => {
            window.removeEventListener("focus", onFocus);
            window.removeEventListener("blur", onBlur);
        };
    }, []);

    // Handle post messages from the KME iframe
    const onSessionEndMemoized = useEventHandler(onSessionEnd);
    const onToggleFullScreenMemoized = useEventHandler(onToggleFullScreen);
    const hasToggleFullScreenCallback = !!onToggleFullScreen;

    useEffect(() => {
        const handler = (e: MessageEvent) => {
            // customize the KME - add custom actions to be handled by kms
            if (
                e.data.id === "unisphere.services.iframes.query"
                && e.data.payload?.id === "unisphere.query.kme.customization"
            ) {
                // custom fullScreen toggle
                const payload = hasToggleFullScreenCallback
                    ? { toggleFullScreen: { controlledByHost: true, isFullScreen: false } }
                    : {};

                // custom actions to be handled by custom components
                const response = {
                    type: "unisphere.query.kme.customization",
                    payload: Object.assign(payload, ...customKmeItems.map((obj) => obj.item)),
                };

                iframeRef.current?.contentWindow?.postMessage(response, "*");
            }

            // handle KME events from custom actions
            if (e.data.id === "unisphere.services.iframes.event") {
                // custom fullScreen toggle
                if (e.data.payload?.id === "unisphere.event.kme.requestToggleFullScreen") {
                    const requested = e.data.payload.isFullScreen;
                    const ack = {
                        type: "unisphere.event.kme.requestToggleFullScreen",
                        payload: { isFullScreen: requested },
                    };
                    iframeRef.current?.contentWindow?.postMessage(ack, "*");

                    onToggleFullScreenMemoized?.(requested);
                }
                // custom actions
                else {
                    const customKmeItem = customKmeItems.find((item) => item.event === e.data.payload?.id);
                    customKmeItem && customKmeItem.component && setCustomComponent(customKmeItem?.component);
                }
            }

            if (e.data.type === "session-end") {
                onSessionEndMemoized(e.data.originEvent === "instructorEndedSessionAll");
            }
        };

        window.addEventListener("message", handler);

        return () => {
            window.removeEventListener("message", handler);
        };
    }, [onSessionEndMemoized, onToggleFullScreenMemoized, hasToggleFullScreenCallback]);

    const handleCustomComponentClose = () => {
        setCustomComponent(undefined);
    };

    return (
        <>
            <FullSizeIframe
                title={title}
                className={"chromatic-ignore"}
                allow="microphone *; camera *; speakers *; usermedia *; autoplay *; fullscreen *; display-capture *;"
                allowFullScreen={true}
                src={roomUrl}
                ref={iframeRef}
            />
            {customComponent && <CustomComponent component={customComponent} onClose={handleCustomComponentClose} />}
        </>
    );
};
